import { UnkeyedDocument } from '@/hooks/useGetStructuredBrowseData';

import Constants from '@/constants';

import { BaseReferenceDTO as LegacyBaseReferenceDTO } from '@/services/content-api-types';

import {
  BaseReferenceDto as BaseReferenceDTO,
  ContentReference,
  DocumentDto,
  SearchDocument as DocumentDTO,
} from '@/_generated/api-clients/xp';
import { store as jotaiStore } from '@/atoms';
import { userPrefsAtom } from '@/atoms/userPrefs';

export const isBaseReferenceDTO = (
  doc: DocumentDTO | BaseReferenceDTO | UnkeyedDocument,
): doc is BaseReferenceDTO =>
  (doc as BaseReferenceDTO).documentId !== undefined;

export const isLegacyBaseReferenceDTO = (
  doc: BaseReferenceDTO | UnkeyedDocument,
): doc is LegacyBaseReferenceDTO =>
  isBaseReferenceDTO(doc) &&
  (doc as LegacyBaseReferenceDTO).fileId !== undefined;

export const isDocument = (
  doc: DocumentDTO | BaseReferenceDTO | UnkeyedDocument,
): doc is DocumentDTO => (doc as DocumentDTO).sourceSystem !== undefined;

export const generateUrlFromReference = (
  reference: LegacyBaseReferenceDTO,
): string =>
  `${Constants.PATHWAY_API_CONTENT_URL}/${reference.versionId}/${reference.fileId}/index.html`;

export const isCategoryDocument = (doc: unknown): doc is DocumentDto => {
  if (typeof doc !== 'object') return false;

  const maybe = doc as DocumentDto;

  // `DocumentDto.name` is a map of language codes to string (object), not an array/string
  if (typeof maybe.name === 'object' && !Array.isArray(maybe.name)) {
    return true;
  }

  // `DocumentDto.references` is a map of language codes to BaseReferenceDto
  if (
    typeof maybe.references === 'object' &&
    !Array.isArray(maybe.references)
  ) {
    // ensure at least one value looks like a BaseReferenceDto (has documentId or id)
    const vals = Object.values(
      maybe.references as Record<string, BaseReferenceDTO>,
    );
    if (vals.length === 0) return true;
    const first = vals[0];
    return (
      typeof first === 'object' && ('documentId' in first || 'id' in first)
    );
  }

  return false;
};

export const getDocumentIdFromCategoryResource = (
  doc: DocumentDto,
): string | undefined => {
  const userPrefs = jotaiStore.get(userPrefsAtom);
  const preferredLanguage = userPrefs.language;

  // Try to find a reference that matches the preferred language
  if (preferredLanguage) {
    return doc.references?.[preferredLanguage]?.documentId;
  }
  return undefined;
};

// temp fix to test video url
export const tempProcessVideoSrc = (src?: string): string | undefined => {
  if (!src) {
    return src;
  }
  return src.replace('qoayFeOmM', 'default');
};

export const getContentUrlBySourceType = (
  document?: DocumentDTO | ContentReference | UnkeyedDocument,
) => {
  if (
    document?.sourceSystem?.toUpperCase() ===
    Constants.DOCUMENT_TYPES.XYLEME.toUpperCase()
  ) {
    // Xyleme cdn contentUrl still has minor issues: like link in content open in new tab but should in modal
    return document.contentApiUrl ?? document.contentUrl;
  }
  return document?.contentUrl ?? document?.contentApiUrl;
};

export const isDirectVideoUrl = (url?: string | null): url is string =>
  !!url &&
  (() => {
    const normalizedUrl = url.toLowerCase();
    return (
      normalizedUrl.includes('players.brightcove.net') ||
      normalizedUrl.includes('/brightcove/') ||
      /\.m3u8(?:\?.*)?$/.test(normalizedUrl) ||
      /\.mp4(?:\?.*)?$/.test(normalizedUrl)
    );
  })();

export const extractVideoUrlFromResponse = (
  videoDetail: string,
): string | undefined => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(videoDetail, 'text/html');

  const candidates = [
    doc.querySelector('iframe')?.getAttribute('src'),
    doc.querySelector('video')?.getAttribute('src'),
    doc.querySelector('video source')?.getAttribute('src'),
    doc.querySelector('source')?.getAttribute('src'),
  ];

  const nestedUrl = candidates.find(isDirectVideoUrl);
  if (nestedUrl) {
    return nestedUrl;
  }

  const unescapedHtml = videoDetail.replaceAll('\\/', '/');
  const unescapedUrlMatch =
    /(https?:\/\/[^\s"'<>]*(players\.brightcove\.net|brightcove|\.m3u8|\.mp4)[^\s"'<>]*)/.exec(
      unescapedHtml,
    );

  if (unescapedUrlMatch?.[1]) {
    return unescapedUrlMatch[1];
  }

  const urlMatch =
    /(https?:\/\/[^\s"'<>]*(players\.brightcove\.net|brightcove|\.m3u8|\.mp4)[^\s"'<>]*)/.exec(
      videoDetail,
    );

  return urlMatch?.[1];
};

export const resolveDirectVideoUrl = async (
  candidateVideoUrl: string,
): Promise<string | undefined> => {
  if (isDirectVideoUrl(candidateVideoUrl)) {
    return candidateVideoUrl;
  }

  const response = await fetch(candidateVideoUrl, {
    credentials: 'include',
  });

  if (!response.ok) {
    return undefined;
  }

  const html = await response.text();
  return extractVideoUrlFromResponse(html);
};
